import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, PencilIcon, TrashIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import EntityHeader from "../../components/crm/EntityHeader";
import {
  DangerButton,
  ErrorBanner,
  SecondaryButton,
} from "../../components/crm/Form";
import PlanForm from "../../components/billing/PlanForm";
import { useAuth } from "../../contexts/AuthContext";
import api, { formatApiError } from "../../lib/api";

const WRITE_ROLES = ["owner", "ops"];
const CADENCE_LABEL = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  annual: "Annual",
};

function fmtCents(cents) {
  return `$${(cents / 100).toFixed(2)}`;
}

export default function PlanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [plan, setPlan] = useState(null);
  const [err, setErr] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get(`/protection-plans/${id}`);
      setPlan(data);
    } catch (e) {
      setErr(formatApiError(e));
    }
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const remove = async () => {
    if (
      !window.confirm(
        `Delete plan "${plan.name}"? This will fail if any active subscription references it.`
      )
    )
      return;
    try {
      await api.delete(`/protection-plans/${id}`);
      toast.success("Plan deleted");
      nav("/protection-plans");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!plan) {
    return (
      <Layout title="Plan" subtitle="Loading">
        <ErrorBanner message={err} />
      </Layout>
    );
  }

  return (
    <Layout title={plan.name} subtitle="Plan detail">
      <button
        onClick={() => nav("/protection-plans")}
        className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-700 hover:text-amber-700 mb-4"
        data-testid="back-to-plans"
      >
        <ArrowLeftIcon size={12} weight="bold" /> All Plans
      </button>

      <EntityHeader
        overline={plan.code}
        title={plan.name}
        subtitle={plan.description || "No description"}
        actions={
          canWrite ? (
            <div className="flex gap-2">
              <SecondaryButton
                onClick={() => setEditOpen(true)}
                data-testid="plan-edit-button"
              >
                <PencilIcon size={12} weight="bold" /> Edit
              </SecondaryButton>
              <DangerButton onClick={remove} data-testid="plan-delete-button">
                <TrashIcon size={12} weight="bold" /> Delete
              </DangerButton>
            </div>
          ) : null
        }
      />

      <ErrorBanner message={err} />

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        <div className="lg:col-span-2 space-y-4">
          <section
            data-testid="plan-summary"
            className="rounded-md border border-slate-200 bg-white p-5 shadow-card"
          >
            <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
              Pricing
            </div>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <Detail label="Code" value={plan.code} mono />
              <Detail
                label="Cadence"
                value={CADENCE_LABEL[plan.default_billing_cadence]}
              />
              <Detail
                label="Default amount"
                value={fmtCents(plan.default_amount_cents)}
                mono
              />
              <Detail label="Currency" value={plan.default_currency} />
              <Detail
                label="Status"
                value={plan.is_active ? "Active" : "Inactive"}
              />
              <Detail
                label="Active subs on this plan"
                value={plan.active_subscription_count}
                mono
              />
            </dl>
          </section>

          <section
            data-testid="plan-features"
            className="rounded-md border border-slate-200 bg-white p-5 shadow-card"
          >
            <div className="font-heading text-sm font-semibold text-slate-900 mb-3">
              Included features
            </div>
            {plan.included_features?.length > 0 ? (
              <ul className="space-y-1.5 text-sm text-slate-700 list-disc list-inside">
                {plan.included_features.map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
            ) : (
              <div className="text-xs text-slate-500">
                No features listed yet — edit to add bullet points.
              </div>
            )}
          </section>
        </div>

        <aside
          className="rounded-md border border-slate-200 bg-white p-5 text-xs text-slate-700 space-y-2"
          data-testid="plan-meta"
        >
          <div className="font-heading text-sm font-semibold text-slate-900 mb-2">
            Catalog metadata
          </div>
          <div>
            <span className="text-slate-500">Created:</span>{" "}
            {new Date(plan.created_at).toLocaleString()}
          </div>
          <div>
            <span className="text-slate-500">Updated:</span>{" "}
            {new Date(plan.updated_at).toLocaleString()}
          </div>
          <div className="pt-2 text-[11px] text-slate-500 leading-relaxed border-t border-slate-200 mt-3">
            Editing default pricing here does <strong>not</strong> change any
            existing subscription. Each subscription stores its own price
            snapshot at enrollment.
          </div>
        </aside>
      </div>

      {canWrite && (
        <PlanForm
          open={editOpen}
          onClose={() => setEditOpen(false)}
          plan={plan}
          onSaved={(p) => {
            setPlan(p);
            setEditOpen(false);
          }}
        />
      )}
    </Layout>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {label}
      </dt>
      <dd
        className={`text-slate-900 mt-0.5 ${mono ? "font-data" : "font-medium"}`}
      >
        {value}
      </dd>
    </div>
  );
}
